import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import env from '../utils/env';
import './FeedbackManagement.css';

interface Feedback {
    _id: string;
    userId: string;
    userEmail: string;
    username: string;
    subject: string;
    message: string;
    type: 'bug' | 'feature' | 'general' | 'improvement';
    priority: 'low' | 'medium' | 'high';
    status: 'open' | 'in_progress' | 'resolved' | 'closed';
    adminNotes?: string;
    createdAt: string;
    updatedAt: string;
    resolvedAt?: string;
    resolvedBy?: string;
}

interface PaginationMetadata {
    totalDocuments: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
}

interface FeedbackResponse {
    feedback: Feedback[];
    pagination: PaginationMetadata;
}

const FeedbackManagement: React.FC = () => {
    const { token, isAdmin } = useAuth();
    const [feedback, setFeedback] = useState<Feedback[]>([]);
    const [pagination, setPagination] = useState<PaginationMetadata | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    
    // Filters
    const [filters, setFilters] = useState({
        status: '',
        type: '',
        priority: '',
        page: 1,
        limit: 10,
        sort: '-createdAt'
    });
    
    // Selected feedback for editing
    const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [updateData, setUpdateData] = useState({
        status: '',
        priority: '',
        adminNotes: ''
    });
    const [updating, setUpdating] = useState(false);

    const fetchFeedback = async () => {
        if (!token) return;
        
        setLoading(true);
        setError(null);
        
        try {
            const queryParams = new URLSearchParams();
            Object.entries(filters).forEach(([key, value]) => {
                if (value) queryParams.append(key, value.toString());
            });
            
            const response = await axios.get<FeedbackResponse>(
                `${env.apiUrl}/admin/feedback?${queryParams.toString()}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
            
            setFeedback(response.data.feedback);
            setPagination(response.data.pagination);
        } catch (err: any) {
            console.error('Error fetching feedback:', err);
            setError(err.response?.data?.error || 'Failed to fetch feedback');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isAdmin && token) {
            fetchFeedback();
        }
    }, [isAdmin, token, filters]);

    const handleFilterChange = (key: string, value: string | number) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
            page: key === 'page' ? Number(value) : 1 // Reset to page 1 when changing filters
        }));
    };

    const handleEditFeedback = (feedbackItem: Feedback) => {
        setSelectedFeedback(feedbackItem);
        setUpdateData({
            status: feedbackItem.status,
            priority: feedbackItem.priority,
            adminNotes: feedbackItem.adminNotes || ''
        });
        setEditModalOpen(true);
    };

    const handleUpdateFeedback = async () => {
        if (!selectedFeedback || !token) return;
        
        setUpdating(true);
        
        try {
            const response = await axios.put(
                `${env.apiUrl}/admin/feedback/${selectedFeedback._id}`,
                updateData,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
            
            // Update the feedback in the list
            setFeedback(prev => prev.map(item => 
                item._id === selectedFeedback._id ? response.data.feedback : item
            ));
            
            setEditModalOpen(false);
            setSelectedFeedback(null);
        } catch (err: any) {
            console.error('Error updating feedback:', err);
            setError(err.response?.data?.error || 'Failed to update feedback');
        } finally {
            setUpdating(false);
        }
    };

    const getPriorityColor = (priority: string) => {
        switch (priority) {
            case 'high': return '#dc3545';
            case 'medium': return '#ffc107';
            case 'low': return '#28a745';
            default: return '#6c757d';
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'open': return '#007bff';
            case 'in_progress': return '#fd7e14';
            case 'resolved': return '#28a745';
            case 'closed': return '#6c757d';
            default: return '#6c757d';
        }
    };

    const getTypeIcon = (type: string) => {
        switch (type) {
            case 'bug': return 'fa-bug';
            case 'feature': return 'fa-lightbulb';
            case 'improvement': return 'fa-arrow-up';
            case 'general': return 'fa-comment';
            default: return 'fa-comment';
        }
    };

    if (!isAdmin) {
        return (
            <div className="feedback-management">
                <div className="access-denied">
                    <h2>Access Denied</h2>
                    <p>You need admin privileges to access feedback management.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="feedback-management">
            <div className="feedback-management-header">
                <h1>
                    <i className="fas fa-comments"></i>
                    Feedback Management
                </h1>
                <p>Manage and respond to user feedback</p>
            </div>

            {/* Filters */}
            <div className="feedback-filters">
                <div className="filter-group">
                    <label>Status:</label>
                    <select 
                        value={filters.status} 
                        onChange={(e) => handleFilterChange('status', e.target.value)}
                    >
                        <option value="">All Status</option>
                        <option value="open">Open</option>
                        <option value="in_progress">In Progress</option>
                        <option value="resolved">Resolved</option>
                        <option value="closed">Closed</option>
                    </select>
                </div>

                <div className="filter-group">
                    <label>Type:</label>
                    <select 
                        value={filters.type} 
                        onChange={(e) => handleFilterChange('type', e.target.value)}
                    >
                        <option value="">All Types</option>
                        <option value="bug">Bug Report</option>
                        <option value="feature">Feature Request</option>
                        <option value="improvement">Improvement</option>
                        <option value="general">General</option>
                    </select>
                </div>

                <div className="filter-group">
                    <label>Priority:</label>
                    <select 
                        value={filters.priority} 
                        onChange={(e) => handleFilterChange('priority', e.target.value)}
                    >
                        <option value="">All Priorities</option>
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                    </select>
                </div>

                <div className="filter-group">
                    <label>Items per page:</label>
                    <select 
                        value={filters.limit} 
                        onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                    >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                    </select>
                </div>

                <button onClick={fetchFeedback} className="refresh-btn">
                    <i className="fas fa-sync-alt"></i>
                    Refresh
                </button>
            </div>

            {error && (
                <div className="error-message">
                    <i className="fas fa-exclamation-triangle"></i>
                    {error}
                </div>
            )}

            {loading ? (
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Loading feedback...</p>
                </div>
            ) : (
                <>
                    {/* Feedback List */}
                    <div className="feedback-list">
                        {feedback.length === 0 ? (
                            <div className="no-feedback">
                                <i className="fas fa-inbox"></i>
                                <p>No feedback found matching your filters.</p>
                            </div>
                        ) : (
                            feedback.map((item) => (
                                <div key={item._id} className="feedback-item">
                                    <div className="feedback-item-header">
                                        <div className="feedback-meta">
                                            <span className={`feedback-type-badge ${item.type}`}>
                                                <i className={`fas ${getTypeIcon(item.type)}`}></i>
                                                {item.type.toUpperCase()}
                                            </span>
                                            <span 
                                                className="feedback-priority-badge"
                                                style={{ backgroundColor: getPriorityColor(item.priority) }}
                                            >
                                                {item.priority.toUpperCase()}
                                            </span>
                                            <span 
                                                className="feedback-status-badge"
                                                style={{ backgroundColor: getStatusColor(item.status) }}
                                            >
                                                {item.status.replace('_', ' ').toUpperCase()}
                                            </span>
                                        </div>
                                        <div className="feedback-actions">
                                            <button 
                                                onClick={() => handleEditFeedback(item)}
                                                className="edit-btn"
                                            >
                                                <i className="fas fa-edit"></i>
                                                Edit
                                            </button>
                                        </div>
                                    </div>

                                    <div className="feedback-content">
                                        <h3>{item.subject}</h3>
                                        <p className="feedback-message">{item.message}</p>
                                        
                                        <div className="feedback-user-info">
                                            <span>
                                                <i className="fas fa-user"></i>
                                                {item.username} ({item.userEmail})
                                            </span>
                                            <span>
                                                <i className="fas fa-calendar"></i>
                                                {new Date(item.createdAt).toLocaleString()}
                                            </span>
                                        </div>

                                        {item.adminNotes && (
                                            <div className="admin-notes">
                                                <h4>Admin Notes:</h4>
                                                <p>{item.adminNotes}</p>
                                            </div>
                                        )}

                                        {item.resolvedAt && (
                                            <div className="resolved-info">
                                                <span>
                                                    Resolved on {new Date(item.resolvedAt).toLocaleString()}
                                                    {item.resolvedBy && ` by ${item.resolvedBy}`}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Pagination */}
                    {pagination && pagination.totalPages > 1 && (
                        <div className="pagination">
                            <button 
                                onClick={() => handleFilterChange('page', filters.page - 1)}
                                disabled={!pagination.hasPreviousPage}
                            >
                                <i className="fas fa-chevron-left"></i>
                                Previous
                            </button>
                            
                            <span className="page-info">
                                Page {pagination.currentPage} of {pagination.totalPages}
                                ({pagination.totalDocuments} total items)
                            </span>
                            
                            <button 
                                onClick={() => handleFilterChange('page', filters.page + 1)}
                                disabled={!pagination.hasNextPage}
                            >
                                Next
                                <i className="fas fa-chevron-right"></i>
                            </button>
                        </div>
                    )}
                </>
            )}

            {/* Edit Modal */}
            {editModalOpen && selectedFeedback && (
                <div className="modal-overlay">
                    <div className="modal">
                        <div className="modal-header">
                            <h3>Edit Feedback</h3>
                            <button 
                                onClick={() => setEditModalOpen(false)}
                                className="close-btn"
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>

                        <div className="modal-content">
                            <div className="feedback-details">
                                <h4>{selectedFeedback.subject}</h4>
                                <p><strong>From:</strong> {selectedFeedback.username} ({selectedFeedback.userEmail})</p>
                                <p><strong>Type:</strong> {selectedFeedback.type}</p>
                                <p><strong>Created:</strong> {new Date(selectedFeedback.createdAt).toLocaleString()}</p>
                                <div className="message-box">
                                    <strong>Message:</strong>
                                    <p>{selectedFeedback.message}</p>
                                </div>
                            </div>

                            <div className="edit-form">
                                <div className="form-group">
                                    <label>Status:</label>
                                    <select 
                                        value={updateData.status}
                                        onChange={(e) => setUpdateData(prev => ({ ...prev, status: e.target.value }))}
                                    >
                                        <option value="open">Open</option>
                                        <option value="in_progress">In Progress</option>
                                        <option value="resolved">Resolved</option>
                                        <option value="closed">Closed</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Priority:</label>
                                    <select 
                                        value={updateData.priority}
                                        onChange={(e) => setUpdateData(prev => ({ ...prev, priority: e.target.value }))}
                                    >
                                        <option value="low">Low</option>
                                        <option value="medium">Medium</option>
                                        <option value="high">High</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Admin Notes:</label>
                                    <textarea
                                        value={updateData.adminNotes}
                                        onChange={(e) => setUpdateData(prev => ({ ...prev, adminNotes: e.target.value }))}
                                        placeholder="Add internal notes about this feedback..."
                                        rows={4}
                                        maxLength={1000}
                                    />
                                    <span className="char-count">{updateData.adminNotes.length}/1000</span>
                                </div>
                            </div>
                        </div>

                        <div className="modal-actions">
                            <button 
                                onClick={() => setEditModalOpen(false)}
                                className="cancel-btn"
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={handleUpdateFeedback}
                                disabled={updating}
                                className="save-btn"
                            >
                                {updating ? (
                                    <>
                                        <i className="fas fa-spinner fa-spin"></i>
                                        Updating...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-save"></i>
                                        Save Changes
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FeedbackManagement;